import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Upload, Lock, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPage = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_auth', 'true');
        toast({
          title: 'Access Granted',
          description: 'Welcome to the admin panel'
        });
      } else {
        toast({
          title: 'Access Denied',
          description: 'Incorrect password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Unable to verify credentials',
        variant: 'destructive'
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    setPassword('');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Simulate upload
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
      toast({
        title: 'Upload Successful',
        description: `${file.name} has been uploaded.`
      });
      event.target.value = '';
    }, 1500);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C9A64A]/20 mb-4">
              <Lock className="h-8 w-8 text-[#C9A64A]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Access</h1>
            <p className="text-gray-400">Enter password to manage resources</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border border-white/10">
            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-semibold uppercase text-[#C9A64A] mb-2 tracking-wider">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#C9A64A] outline-none text-white"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-bold"
              >
                {loginLoading ? 'Verifying...' : 'Login'}
              </Button>
              <Link to="/">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-white/10 text-white hover:bg-white/5"
                >
                  Back to Home
                </Button>
              </Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-6 bg-[#0F1A2A]/95 backdrop-blur-xl border-b border-white/10 shadow-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-7 w-7 text-[#C9A64A]" />
              <BarChart3 className="h-4 w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">
              Virtual<span className="text-[#C9A64A]">CFO</span> <span className="text-xs text-gray-400">Admin</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" className="gap-2 border-white/10 text-white hover:bg-white/5">
                <ArrowLeft className="h-4 w-4" />
                Home
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 text-white hover:bg-white/5">
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 mb-8 px-6 py-3 bg-[#C9A64A]/20 rounded-full border border-[#C9A64A]/30">
            <Lock className="h-5 w-5 text-[#C9A64A]" />
            <span className="text-[#C9A64A] text-sm font-bold tracking-widest uppercase">Admin Panel</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-6">Resource Management</h1>
          <p className="text-xl text-gray-300 font-light mb-12">
            Upload presentations, templates, and financial resources.
          </p>

          {/* Upload Section */}
          <div className="bg-white/5 backdrop-blur-xl p-8 rounded-2xl border-2 border-dashed border-[#C9A64A]/30 hover:border-[#C9A64A] transition-all duration-300 text-center mb-8">
            <Upload className="h-12 w-12 mx-auto mb-4 text-[#C9A64A]" />
            <h3 className="text-2xl font-bold text-white mb-2">Upload New Resource</h3>
            <p className="text-gray-400 mb-6">Add financial models, templates, presentations, and guides</p>
            
            <input
              type="file"
              id="admin-file-upload"
              className="hidden"
              onChange={handleFileUpload}
              accept=".ppt,.pptx,.xls,.xlsx,.pdf"
              disabled={uploading}
            />
            
            <label htmlFor="admin-file-upload">
              <Button
                as="span"
                disabled={uploading}
                className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 font-bold cursor-pointer"
              >
                {uploading ? 'Uploading...' : 'Choose File to Upload'}
              </Button>
            </label>
            
            <p className="text-sm text-gray-400 mt-4">
              Supported: PPT, PPTX, XLS, XLSX, PDF • Max size: 50MB
            </p>
          </div>

          {/* Quick Links */}
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/presentations">
              <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 hover:border-[#C9A64A]/50 transition-all duration-300">
                <FileText className="h-8 w-8 text-[#C9A64A] mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">View Presentations</h3>
                <p className="text-sm text-gray-400">See public presentation page</p>
              </div>
            </Link>
            <Link to="/free-resources">
              <div className="bg-white/5 backdrop-blur-xl p-6 rounded-2xl border border-white/10 hover:border-[#C9A64A]/50 transition-all duration-300">
                <FileText className="h-8 w-8 text-[#C9A64A] mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">View Downloads</h3>
                <p className="text-sm text-gray-400">See public downloads page</p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
